/**
 * Sessions Page - View and manage monitoring sessions
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useSessionsStore } from '../stores/sessionsStore';
import { useDiaperStore } from '../stores/diaperStore';
import { useEnvironmentStore } from '../stores/environmentStore';
import { useToast } from '../stores/toastStore';
import { generateMACAddresses } from '../lib/utils';
import { loadSessions as apiLoadSessions } from '../lib/api';
import type { Session } from '../lib/types';
import { SessionInfoCard } from '../components/SessionInfoCard';
import { SiteTreeSelect } from '../components/SiteTreeSelect';
import {
  ConfigAccount,
  accountDisplayLabel,
  buildAvailableSiteIds,
  extractSiteIds,
  loadAccountSiteTree,
  loadEnabledConfigAccounts,
  resolveNovaAccountIdForSite,
} from '../lib/novaAccounts';
import type { SiteTreeNode } from '../lib/siteTree';
import {
  buildSiteNameMap,
  buildVisibleSiteRows,
  siteLabel,
} from '../lib/siteTree';

export const SessionsPage: React.FC = () => {
  const authSites = useAuthStore((state) => state.sites);
  const { features } = useEnvironmentStore();
  const {
    activeSessions,
    currentSite,
    currentNovaAccountId,
    isLoading,
    error,
    setCurrentSite,
    setCurrentNovaAccountId,
    clearSessions,
    loadSessions,
    createSession,
    deleteSession,
  } = useSessionsStore();
  const { getDiaperModel, loadDiaperModels } = useDiaperStore();
  const toast = useToast();
  const navigate = useNavigate();

  const [clipAddress, setClipAddress] = useState('');
  const [clipName, setClipName] = useState('');
  const [clientName, setClientName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [configAccounts, setConfigAccounts] = useState<ConfigAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [siteTree, setSiteTree] = useState<SiteTreeNode[]>([]);
  const [siteTreeLoading, setSiteTreeLoading] = useState(false);
  const [siteTreeError, setSiteTreeError] = useState<string | null>(null);
  const [accountSessions, setAccountSessions] = useState<Session[]>([]);
  const [accountSessionsLoading, setAccountSessionsLoading] = useState(false);
  const [accountSessionsError, setAccountSessionsError] = useState<string | null>(null);

  const macAddresses = generateMACAddresses(150);
  const availableSiteIds = useMemo(
    () => buildAvailableSiteIds(authSites, configAccounts, selectedAccountId, siteTree),
    [authSites, configAccounts, selectedAccountId, siteTree]
  );
  const activeAccountId = selectedAccountId || currentNovaAccountId || '';
  const siteNamesById = useMemo(() => buildSiteNameMap(siteTree), [siteTree]);
  const siteRows = useMemo(
    () => buildVisibleSiteRows(siteTree, availableSiteIds),
    [availableSiteIds, siteTree]
  );
  const accountScopedSiteIds = useMemo(() => {
    if (!activeAccountId) return [];
    const account = configAccounts.find((item) => item.id === activeAccountId);
    const accountSites = extractSiteIds(account);
    if (accountSites.length > 0) return accountSites;
    return currentSite ? [currentSite] : [];
  }, [activeAccountId, configAccounts, currentSite]);
  const usedSessionsByMac = useMemo(() => {
    const sessions = accountSessions.length > 0 ? accountSessions : activeSessions;
    const next = new Map<string, Session[]>();
    for (const session of sessions) {
      const key = session.clipAddress?.toUpperCase();
      if (!key || session.monitored === false) continue;
      const existing = next.get(key) || [];
      existing.push(session);
      next.set(key, existing);
    }
    return next;
  }, [accountSessions, activeSessions]);

  const loadConfigAccounts = useCallback(async () => {
    try {
      const enabledAccounts = await loadEnabledConfigAccounts();
      setConfigAccounts(enabledAccounts);
    } catch (loadError) {
      console.error('Failed to load Nova accounts for sessions page:', loadError);
    }
  }, []);

  const loadAccountSessions = useCallback(async () => {
    if (!activeAccountId || accountScopedSiteIds.length === 0) {
      setAccountSessions([]);
      setAccountSessionsError(null);
      return;
    }

    setAccountSessionsLoading(true);
    setAccountSessionsError(null);
    try {
      const data = await apiLoadSessions(accountScopedSiteIds, { novaAccountId: activeAccountId });
      setAccountSessions(data.statuses || []);
    } catch (loadError: any) {
      console.error('Failed to load account sessions:', loadError);
      setAccountSessions([]);
      setAccountSessionsError(loadError?.message || 'Failed to check clips across account');
    } finally {
      setAccountSessionsLoading(false);
    }
  }, [accountScopedSiteIds, activeAccountId]);

  useEffect(() => {
    loadConfigAccounts();
  }, [loadConfigAccounts]);

  useEffect(() => {
    if (!activeAccountId) {
      setSiteTree([]);
      setSiteTreeError(null);
      setSiteTreeLoading(false);
      return;
    }

    let cancelled = false;
    setSiteTreeLoading(true);
    setSiteTreeError(null);
    loadAccountSiteTree(activeAccountId)
      .then((tree) => {
        if (!cancelled) setSiteTree(tree);
      })
      .catch((loadError) => {
        if (cancelled) return;
        console.error('Failed to load Nova organization tree:', loadError);
        setSiteTree([]);
        setSiteTreeError(loadError?.message || 'Failed to load organization names');
      })
      .finally(() => {
        if (!cancelled) setSiteTreeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeAccountId]);

  useEffect(() => {
    loadAccountSessions();
  }, [loadAccountSessions]);

  useEffect(() => {
    if (!selectedAccountId) return;
    if (configAccounts.some((account) => account.id === selectedAccountId)) return;

    setSelectedAccountId('');
    setCurrentSite(null);
    clearSessions();
  }, [clearSessions, configAccounts, selectedAccountId, setCurrentSite]);

  useEffect(() => {
    const resolvedAccountId = resolveNovaAccountIdForSite(
      configAccounts,
      selectedAccountId,
      currentSite
    );
    setCurrentNovaAccountId(resolvedAccountId);
  }, [configAccounts, currentSite, selectedAccountId, setCurrentNovaAccountId]);

  useEffect(() => {
    return () => {
      setCurrentNovaAccountId(null);
    };
  }, [setCurrentNovaAccountId]);

  // Diaper-models catalog needs an account context (proxy is account-aware).
  // Cached per-account in the store, so this is a no-op after the first load.
  // `toast` deliberately not in deps — useToast() returns a new object each
  // render which would loop the effect.
  useEffect(() => {
    if (!currentNovaAccountId) return;
    loadDiaperModels(currentNovaAccountId).catch((err) => {
      toast.error(`Failed to load diaper models: ${err?.message || err}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNovaAccountId, loadDiaperModels]);

  useEffect(() => {
    if (currentSite != null && availableSiteIds.includes(currentSite)) {
      return;
    }

    if (availableSiteIds.length > 0) {
      setCurrentSite(availableSiteIds[0]);
    } else {
      setCurrentSite(null);
    }
    clearSessions();
  }, [availableSiteIds, clearSessions, currentSite, setCurrentSite]);

  useEffect(() => {
    if (currentSite) {
      loadSessions();
    }
  }, [currentSite, loadSessions]);

  const handleMACChange = (mac: string) => {
    setClipAddress(mac);
    // Find the index of the selected MAC to determine the clip number
    const clipIndex = macAddresses.findIndex(m => m === mac);
    if (clipIndex !== -1) {
      const clipNumber = clipIndex + 1;
      // Format as serial number: CLIP-0001, CLIP-0002, etc.
      const serialNumber = `CLIP-${clipNumber.toString().padStart(4, '0')}`;
      setClipName(serialNumber);
      setClientName(`Room ${clipNumber}`);
    }
  };

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clipAddress || !clipName || !clientName) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCreating(true);
    try {
      // No initial packet sent - matches Android app behavior
      // Wait for real clip (or simulator) to send first packet
      const sessionId = await createSession({
        clipAddress,
        clipName,
        clientName,
      });
      toast.success(`Session created successfully! Session ID: ${sessionId}`);
      
      // Reset form
      setClipAddress('');
      setClipName('');
      setClientName('');
      setTimeout(() => loadAccountSessions(), 1200);
    } catch (error: any) {
      toast.error(`Failed to create session: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteSession = async (sessionId: number) => {
    if (!window.confirm('Are you sure you want to close/delete this session?')) {
      return;
    }

    try {
      await deleteSession(sessionId);
      toast.success(`Session ${sessionId} closed successfully!`);
      setTimeout(() => loadAccountSessions(), 800);
    } catch (error: any) {
      toast.error(`Failed to delete session: ${error.message}`);
    }
  };

  const handleAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    clearSessions();
    setCurrentSite(null);
  };

  const handleSimulate = (sessionId: number) => {
    const params = new URLSearchParams({ session: String(sessionId) });
    if (currentNovaAccountId) {
      params.set('account', currentNovaAccountId);
    }
    navigate(`/simulator?${params.toString()}`);
  };

  return (
    <div className="space-y-6">
      {/* Active Sessions Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">Active Sessions</h2>
        
        <p className="text-gray-600 mb-4 bg-blue-50 border-l-4 border-primary p-3 rounded">
          💡 A <strong>session</strong> represents a care period with a citizen. Each session links a
          clip (monitoring device) to a specific patient/room and tracks wetness events from the moment
          the diaper is attached until it's changed or the session ends.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 mb-4">
          <select
            value={selectedAccountId}
            onChange={(e) => handleAccountChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All accounts</option>
            {configAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {accountDisplayLabel(account)}
              </option>
            ))}
          </select>

          <SiteTreeSelect
            rows={siteRows}
            value={currentSite}
            onChange={setCurrentSite}
            emptyLabel="No sites available"
            disabled={availableSiteIds.length === 0}
          />

          <button
            onClick={() => {
              loadConfigAccounts();
              loadAccountSessions();
              if (currentSite) {
                loadSessions();
              }
            }}
            disabled={isLoading}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-light disabled:opacity-60"
          >
            🔄 Refresh
          </button>
        </div>

        <div className="mb-4 space-y-1 text-xs">
          {currentSite && (
            <p className="text-gray-500">
              {siteTreeLoading
                ? 'Loading site names...'
                : `Selected site: ${siteLabel(currentSite, siteNamesById)}`}
            </p>
          )}
          {accountSessionsLoading && (
            <p className="text-gray-500">Checking clip usage across the selected account...</p>
          )}
          {siteTreeError && <p className="text-amber-700">{siteTreeError}</p>}
          {accountSessionsError && <p className="text-amber-700">{accountSessionsError}</p>}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="text-center py-8 text-gray-600">Loading sessions...</div>
        )}

        {!isLoading && activeSessions.length === 0 && (
          <p className="text-center py-8 text-gray-500">No active sessions found</p>
        )}

        {!isLoading && activeSessions.length > 0 && (
          <div className="space-y-4">
            {activeSessions.map((session) => {
              const diaperModel = session.diaperModelCode ? getDiaperModel(session.diaperModelCode) : null;
              return (
                <div key={session.id} className="space-y-2">
                  <SessionInfoCard
                    session={session}
                    diaperModel={diaperModel}
                    showActions
                    activeSessions={accountSessions.length > 0 ? accountSessions : activeSessions}
                    onActionComplete={() => {
                      loadSessions();
                      loadAccountSessions();
                    }}
                  />
                  
                  {/* Additional Actions (dev only) */}
                  {features.canCreateSessions && (
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => handleSimulate(session.id)}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-light transition-colors font-medium shadow-sm"
                      >
                        Simulate
                      </button>
                      <button
                        onClick={() => handleDeleteSession(session.id)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium shadow-sm"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Session Section (dev only) */}
      {features.canCreateSessions && (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-md p-6 border-2 border-gray-200">
          <div className="text-center mb-6 pb-4 border-b-2 border-gray-200">
            <h2 className="text-2xl font-bold text-primary mb-1">Create New Session</h2>
            <p className="text-gray-600 text-sm">Start monitoring a new client</p>
          </div>

          <form onSubmit={handleCreateSession} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Clip MAC</label>
                <select
                  value={clipAddress}
                  onChange={(e) => handleMACChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select MAC...</option>
                  {macAddresses.map((mac, idx) => {
                    const usedSessions = usedSessionsByMac.get(mac.toUpperCase()) || [];
                    const isUsed = usedSessions.length > 0;
                    const usedAt = usedSessions
                      .slice(0, 2)
                      .map((session) => `${siteLabel(session.siteId, siteNamesById)} / ${session.client || `Session ${session.id}`}`)
                      .join(', ');
                    return (
                      <option key={mac} value={mac} disabled={isUsed}>
                        {mac} (Clip {idx + 1}){isUsed ? ` - In use at ${usedAt}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Clip Name</label>
                <input
                  type="text"
                  value={clipName}
                  onChange={(e) => setClipName(e.target.value)}
                  placeholder="e.g., CLIP-0001"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Room/Client</label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Room 101"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="text-sm text-gray-600 bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
              <strong>Note:</strong> This creates a session and connects the clip to the patient/room. 
              No initial packet is sent - the clip (or simulator) will send its first status when attached to a diaper.
            </div>

            <button
              type="submit"
              disabled={isCreating}
              className="w-full py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:opacity-60 transition-all shadow-lg"
            >
              {isCreating ? 'Creating...' : 'Create Session'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
